/* ===========================================================
   pages/Settings.jsx

   Account (name shown to collaborators, shared with Weather Protocol)
   · Mode (Casual / Developer) · Look (match Weather Protocol, or pick
   your own) · Writing (font, page width, spell check) · Developer
   (code time limit) · Storage · This device · Help.
   =========================================================== */

import { useEffect, useState } from 'preact/hooks';
import { session, updateProfile, mode, store, pref } from '../state.js';
import { api } from '../lib/api.js';
import { go } from '../lib/router.js';
import { PRESETS, wpColours, okHex } from '../lib/theme.js';
import { Button, Icon, IconButton, toast, bytes, confirmDialog } from '../ui/ui.jsx';
import { myWordList, loadWords, addWord, removeWord } from '../sync/words.js';
import { TopBar } from './Home.jsx';
import { signOut } from './Gate.jsx';
import { LIMITS } from '../../shared/protocol.js';

const save = patch => updateProfile(patch).catch(err => toast(err, { kind: 'error' }));

function Section({ title, children, id }) {
  return <section class="set-section" aria-labelledby={id}><h2 id={id}>{title}</h2>{children}</section>;
}

function Swatch({ c, on, onClick, label }) {
  return (
    <button type="button" class={`swatch ${on ? 'on' : ''}`} onClick={onClick} aria-pressed={String(!!on)} aria-label={label}>
      <span class="swatch-page" style={{ background: c.bg }}>
        <span style={{ background: c.panel, color: c.fg }}><i style={{ background: c.a }} />Aa</span>
      </span>
      <span class="swatch-name">{label}</span>
    </button>
  );
}

export function Settings() {
  const s = session.value;
  const p = s.profile || {};
  const [name, setName] = useState(p.name || '');
  const [usage, setUsage] = useState(s.usage?.used ?? null);
  const [custom, setCustom] = useState(p.custom || { a: '#2563eb', bg: '#f3f4f6', panel: '#ffffff', fg: '#1f2328' });
  const wp = wpColours(s.wp || {});

  useEffect(() => { api('/api/usage').then(r => setUsage(r.used)).catch(() => {}); }, []);

  const own = p.themeMode === 'own';
  const theme = p.theme || 'light';

  return (
    <div class="home">
      <TopBar title="Settings" />
      <main id="main" class="home-main narrow settings">
        <Section title="Account" id="s-account">
          <p class="hint">You're signed in as <strong>{s.user?.email}</strong>. This is your Weather Protocol account; the same email and password work on both sites.</p>
          <form class="row-form" onSubmit={e => { e.preventDefault(); save({ name: name.trim() }).then(() => toast('Name saved.')); }}>
            <label class="field grow">
              <span>Your name (shown on your cursor when you edit with others)</span>
              <input value={name} maxLength={40} onInput={e => setName(e.target.value)} />
            </label>
            <Button kind="primary" type="submit">Save</Button>
          </form>
          <Button icon="logout" onClick={signOut}>Sign out</Button>
        </Section>

        <Section title="Mode" id="s-mode">
          <p class="hint">Only changes what's shown. It never changes or hides anything in your docs.</p>
          <div class="mode-choices small">
            <button type="button" class={`mode-choice ${mode.value === 'casual' ? 'on' : ''}`} aria-pressed={String(mode.value === 'casual')} onClick={() => save({ mode: 'casual' })}>
              <Icon name="pencil" size={24} /><strong>Casual</strong><span>Writing, notes, school, everyday documents</span>
            </button>
            <button type="button" class={`mode-choice ${mode.value === 'dev' ? 'on' : ''}`} aria-pressed={String(mode.value === 'dev')} onClick={() => save({ mode: 'dev' })}>
              <Icon name="code" size={24} /><strong>Developer</strong><span>Code, Markdown, LaTeX, runnable blocks, sandbox</span>
            </button>
          </div>
        </Section>

        <Section title="Look" id="s-look">
          <label class="check">
            <input type="checkbox" checked={!own} onChange={e => save({ themeMode: e.target.checked ? 'wp' : 'own', ...(e.target.checked ? {} : { theme: p.theme || 'light' }) })} />
            <span>Same look as Weather Protocol <span class="hint-inline">(now: {wp.name || 'Forest'})</span></span>
          </label>
          {own && (
            <>
              <div class="swatches">
                <Swatch c={matchMedia('(prefers-color-scheme: dark)').matches ? PRESETS[1] : PRESETS[0]} on={theme === 'device'} label="Match my device" onClick={() => save({ theme: 'device' })} />
                {PRESETS.map(t => <Swatch key={t.id} c={t} on={theme === t.id} label={t.name} onClick={() => save({ theme: t.id })} />)}
                <Swatch c={custom} on={theme === 'custom'} label="Your own colours" onClick={() => save({ theme: 'custom', custom })} />
              </div>
              {theme === 'custom' && (
                <div class="custom-colours">
                  {[['a', 'Accent'], ['bg', 'Background'], ['panel', 'Page'], ['fg', 'Text']].map(([k, label]) => (
                    <label class="colour">
                      <input type="color" value={custom[k]} onInput={e => setCustom({ ...custom, [k]: e.target.value })}
                        onChange={e => { const c = { ...custom, [k]: e.target.value }; if (Object.values(c).every(okHex)) save({ theme: 'custom', custom: c }); }} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </Section>

        <Section title="Writing" id="s-writing">
          <div class="set-grid">
            <label class="field">
              <span>Font for docs</span>
              <select value={pref('font', 'sans')} onChange={e => save({ prefs: { font: e.target.value } })}>
                <option value="sans">Clean (sans-serif)</option>
                <option value="serif">Classic (serif)</option>
                <option value="mono">Typewriter (monospace)</option>
              </select>
            </label>
            <label class="field">
              <span>Page width</span>
              <select value={pref('width', 'normal')} onChange={e => save({ prefs: { width: e.target.value } })}>
                <option value="normal">Like paper</option>
                <option value="wide">Wide</option>
              </select>
            </label>
          </div>
          <label class="check">
            <input type="checkbox" checked={pref('spellcheck', true)} onChange={e => save({ prefs: { spellcheck: e.target.checked } })} />
            <span>Check spelling while I type</span>
          </label>
          <label class="check">
            <input type="checkbox" checked={pref('autocorrect', true)} onChange={e => save({ prefs: { autocorrect: e.target.checked } })} />
            <span>Fix typos as I type (autocorrect). Ctrl+Z puts your word back.</span>
          </label>
          <Dictionary />
        </Section>

        {mode.value === 'dev' && (
          <Section title="Developer" id="s-dev">
            <label class="field narrow-field">
              <span>Stop code that runs longer than (seconds)</span>
              <input type="number" min="2" max="120" value={pref('timeout', 10)}
                onChange={e => save({ prefs: { timeout: Math.max(2, Math.min(120, Number(e.target.value) || 10)) } })} />
            </label>
            <p class="hint">Python, JavaScript and SQL run inside your browser, walled off from your account. Python's first run downloads about 10 MB, once.</p>
          </Section>
        )}

        <Section title="Storage" id="s-storage">
          {usage === null ? <p class="hint">Checking…</p> : (
            <>
              <div class="meter" role="meter" aria-valuemin="0" aria-valuemax={LIMITS.quotaBytes} aria-valuenow={usage} aria-label="Storage used">
                <span style={{ width: `${Math.min(100, (usage / LIMITS.quotaBytes) * 100)}%` }} />
              </div>
              <p class="hint">{bytes(usage)} of 200 MB used (docs, their history and pictures). <a href="/trash">Trash</a> counts until it's emptied.</p>
            </>
          )}
        </Section>

        <Section title="This device" id="s-device">
          <p class="hint">Your docs are kept on this device too, so they open instantly and work offline.</p>
          <Button icon="trash" kind="danger-plain" onClick={async () => {
            const pending = await store.value?.pendingDocs().catch(() => []) || [];
            if (pending.length) { toast('Some changes haven\'t synced yet. Connect to the internet first, then try again.'); return; }
            if (!await confirmDialog({ title: 'Clear this device\'s copy?', text: 'Your docs stay safe in your account; they\'ll download again when you open them.', ok: 'Clear', danger: true })) return;
            await store.value.destroy();
            location.reload();
          }}>Clear this device's copy</Button>
        </Section>

        <Section title="Help" id="s-help">
          <div class="row-gap">
            <Button icon="sparkle" onClick={() => go('/welcome')}>Replay the welcome tour</Button>
            <Button icon="computer" onClick={() => go('/files')}>Open from computer</Button>
          </div>
          <h3>Keyboard shortcuts</h3>
          <dl class="shortcuts">
            {[['Ctrl K', 'Search docs, headings and actions'], ['Ctrl B / I / U', 'Bold / italic / underline'], ['Ctrl Shift H', 'Highlight'],
              ['Ctrl Alt 1 / 2 / 3', 'Headings'], ['Ctrl Shift 7 / 8 / 9', 'Numbered / bulleted list / checklist'], ['Ctrl Z / Ctrl Y', 'Undo / redo'],
              ['# then space', 'Heading'], ['- then space', 'Bulleted list'], ['[] then space', 'Checklist'], ['> then space', 'Quote']].map(([k, v]) => <><dt><kbd>{k}</kbd></dt><dd>{v}</dd></>)}
          </dl>
        </Section>
      </main>
    </div>
  );
}

/* your own dictionary (his ask, 2026-11-04): words autocorrect and Proofread never touch */
function Dictionary() {
  const [text, setText] = useState('');
  useEffect(() => { loadWords(); }, []);
  const list = myWordList.value;
  const add = async e => {
    e.preventDefault();
    const w = text.trim();
    if (!w) return;
    try { await addWord(w); setText(''); } catch (err) { toast(err, { kind: 'error' }); }
  };
  return (
    <div class="dict">
      <h3 class="dict-h">My dictionary <span class="hint-inline">{list.length ? `${list.length} word${list.length === 1 ? '' : 's'}` : ''}</span></h3>
      <p class="hint">Names, slang and words of your own: autocorrect and Proofread leave these alone. You can also right-click a word in a doc and pick "Add to my dictionary".</p>
      <form class="dict-add" onSubmit={add}>
        <input class="input" type="text" value={text} maxLength={40} placeholder="Add a word" aria-label="Add a word to my dictionary" onInput={e => setText(e.target.value)} />
        <Button size="sm" type="submit" icon="plus" disabled={!text.trim()}>Add</Button>
      </form>
      {list.length > 0 && (
        <ul class="dict-list">
          {list.map(w => <li key={w}><span>{w}</span><IconButton icon="close" label={`Remove ${w}`} size={14} onClick={() => removeWord(w)} /></li>)}
        </ul>
      )}
    </div>
  );
}
